import type { FC, PropsWithChildren } from 'react';
import { useEffect } from 'react';

import posthog from 'posthog-js';
import { PostHogProvider as PostHogProviderBase } from 'posthog-js/react';

import { useGetBillingProfile } from 'entities/billing/api/useGetBillingProfile';
import { useGetWidgetSettings } from 'hooks/customization';
import { useAuth } from 'shared/lib/auth/useAuth';

// Skip init when no key is configured (e.g. the gov build) so posthog-js makes no
// external calls. posthog.capture() elsewhere is a safe no-op on an uninitialized client.
if (import.meta.env.VITE_APP_POSTHOG_API_KEY) {
  posthog.init(import.meta.env.VITE_APP_POSTHOG_API_KEY, {
    api_host: import.meta.env.VITE_APP_POSTHOG_HOST,
    ui_host: 'https://us.posthog.com',
    defaults: '2025-05-24',
  });
}

export const PostHogProvider: FC<PropsWithChildren> = ({ children }) => {
  const { user } = useAuth();
  const isReady = !!user?.is_approved;
  const { data: widgetSettings } = useGetWidgetSettings({ enabled: isReady });
  const { data: billingProfile } = useGetBillingProfile({ enabled: isReady });

  useEffect(() => {
    if (user) {
      const { id, email, profile, company } = user;

      posthog?.identify(id, {
        email,
        name: `${profile?.first_name} ${profile?.last_name?.charAt(0)}`,
        productName: 'dashboard',
        role: profile?.role.id,
        companyType: company?.company_type,
        useCase: widgetSettings?.use_case,
        subscriptionId: billingProfile?.subscription_id,
      });
    }
  }, [user, widgetSettings, billingProfile]);

  return <PostHogProviderBase client={posthog}>{children}</PostHogProviderBase>;
};
